import * as React from "react";
import {
  Button,
  Checkbox,
  Input,
  Spinner,
  Divider,
  Text,
  makeStyles,
} from "@fluentui/react-components";
import { SearchRegular, DismissRegular } from "@fluentui/react-icons";
import { FilterChipWrapper } from "./FilterChipWrapper";

const DEBOUNCE_MS = 300;

const useStyles = makeStyles({
  searchBox: {
    marginBottom: "8px",
  },
  resultsList: {
    maxHeight: "280px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  selectedChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginBottom: "8px",
  },
  chip: {
    borderRadius: "12px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "4px",
    fontSize: "12px",
  },
  hint: {
    padding: "4px 0",
    textAlign: "left" as const,
  },
});

/** Option shape: key (React key), value (DB column value), text (display). */
export interface FilterOptionItem {
  key: string;
  value: string;
  text: string;
}

export interface ServerSearchFilterProps {
  label: string;
  /** Currently applied DB values. */
  value: string[];
  maxLabelLength?: number;
  /** Whether this filter has been modified from its default */
  isModified?: boolean;
  /** Called when the user clicks the reset icon */
  onReset?: () => void;
  /** Called with selected DB values on apply. */
  onApply: (values: string[]) => void;
  /** Server-side search function — returns matching options with key, value, text */
  onSearch: (query: string) => Promise<FilterOptionItem[]>;
}

/**
 * Server search filter — for cardinality > 5000.
 *
 * Tracks **value** (DB column value) internally and returns value[] via onApply.
 * Uses **key** for React keys. Displays **text** as the human-readable label.
 * No upfront load. User types to search, server returns matches.
 */
export const ServerSearchFilter: React.FC<ServerSearchFilterProps> = ({
  label,
  value = [],
  maxLabelLength = 50,
  isModified = false,
  onReset,
  onApply,
  onSearch,
}) => {
  const styles = useStyles();
  const [draft, setDraft] = React.useState<Set<string>>(new Set(value));
  const [search, setSearch] = React.useState("");
  const [results, setResults] = React.useState<FilterOptionItem[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Lookup map: DB value → display text. Populated from search results. */
  const [textByValue, setTextByValue] = React.useState<Map<string, string>>(
    () => new Map()
  );

  React.useEffect(() => {
    setDraft(new Set(value));
  }, [value]);

  // Debounced server search
  React.useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const items = await onSearch(search.trim());
        setResults(items);
        // Merge new results into the lookup map
        setTextByValue((prev) => {
          const next = new Map(prev);
          for (const item of items) next.set(item.value, item.text);
          return next;
        });
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
        setHasSearched(true);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, onSearch]);

  function toggleResult(val: string) {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  }

  function removeSelected(val: string) {
    setDraft((prev) => {
      const next = new Set(prev);
      next.delete(val);
      return next;
    });
  }

  function resetSearchState() {
    setSearch("");
    setResults([]);
    setHasSearched(false);
  }

  function apply() {
    onApply(Array.from(draft));
    resetSearchState();
  }

  function cancel() {
    setDraft(new Set(value));
    resetSearchState();
  }

  function valToText(val: string): string {
    return textByValue.get(val) ?? val;
  }

  // ── Display value for chip ──────────────────────────────────────────────

  function buildDisplayValue(): string {
    if (!value.length) return "All";
    const displayText = value.map(valToText).join(", ");
    const full = `${label}: ${displayText}`;
    if (full.length <= maxLabelLength) return displayText;
    return `${value.length} selected`;
  }

  const isDirty = React.useMemo(() => {
    if (draft.size !== value.length) return true;
    return value.some((v) => !draft.has(v));
  }, [draft, value]);

  const draftArray = Array.from(draft);

  return (
    <FilterChipWrapper
      label={label}
      displayValue={buildDisplayValue()}
      isModified={isModified}
      onReset={onReset ?? (() => {})}
      isDirty={isDirty}
      onApply={apply}
      onCancel={cancel}
      surfaceWidth="320px"
    >
      <Input
        className={styles.searchBox}
        contentBefore={<SearchRegular />}
        placeholder="Search values..."
        value={search}
        onChange={(_, d) => setSearch(d.value)}
        size="small"
      />

      {/* Selected items as removable chips — show display text */}
      {draftArray.length > 0 && (
        <>
          <div className={styles.selectedChips}>
            {draftArray.map((val) => (
              <Button
                key={val}
                size="small"
                appearance="outline"
                className={styles.chip}
                icon={<DismissRegular />}
                iconPosition="after"
                onClick={() => removeSelected(val)}
              >
                {valToText(val)}
              </Button>
            ))}
          </div>
          <Divider />
        </>
      )}

      {/* Search results */}
      <div className={styles.resultsList}>
        {isSearching && (
          <div className={styles.hint}>
            <Spinner size="tiny" label="Searching..." />
          </div>
        )}

        {!isSearching && !hasSearched && !search && draftArray.length === 0 && (
          <div className={styles.hint}>
            <Text size={200}>
              Showing all values, start typing to search...
            </Text>
          </div>
        )}

        {!isSearching && hasSearched && results.length === 0 && (
          <div className={styles.hint}>
            <Text size={200}>No results found</Text>
          </div>
        )}

        {!isSearching &&
          results.map((r) => (
            <Checkbox
              key={r.key}
              label={r.text}
              checked={draft.has(r.value)}
              onChange={() => toggleResult(r.value)}
            />
          ))}
      </div>
    </FilterChipWrapper>
  );
};